import { useAuth0 } from '@auth0/auth0-react';
import {
  type Query,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';

import { API_URL, apiFetch } from '@/config/api';
import type {
  AgentChatCount,
  BuiltinAgents,
  NudgeRecipients,
  NudgeStatus,
  PreviewTarget,
  SendNudgeRequest,
  SendNudgeResult,
  UserAgentChange,
  UserChangeStarted,
  UserChangeStatus,
  UserPreview,
} from '@/features/proactive-agents/proactive-agents-types';
import {
  apiErrorMessage,
  type ApiErrorBody,
} from '@/features/proactivity-experiments/useProactivityExperiments';

// A request the server refused (4xx, with its status) or that got no sign-in token, so it did nothing
class RefusedRequestError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.status = status;
  }
}

// A server error or a dropped connection can come after the work has started
export const mayHaveStarted = (error: Error) =>
  !(error instanceof RefusedRequestError);

// The server knows nothing under the requested id
const isNotFound = (error: Error) =>
  error instanceof RefusedRequestError && error.status === 404;

const useProactiveAgentsApi = () => {
  const { getAccessTokenSilently } = useAuth0();
  return async <T>(path: string, fallback: string, init?: RequestInit) => {
    const token = await getAccessTokenSilently().catch((error: unknown) => {
      throw new RefusedRequestError(
        error instanceof Error ? error.message : 'Could not sign in',
        null
      );
    });
    const response = await apiFetch(
      `${API_URL}/admin/proactive-agents${path}`,
      {
        ...init,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(init?.body && { 'Content-Type': 'application/json' }),
        },
      }
    ).catch(() => {
      // Fetch rejects with a browser-specific message, such as "Failed to fetch", when no response can be read: the
      // network failed, or the response lacks CORS headers. The load balancer's errors and the backend's unhandled
      // 500s lack them.
      throw new Error(
        'No response from the server could be read. The server may have failed, taken too long, or been unreachable.'
      );
    });
    if (!response.ok) {
      const body = (await response
        .json()
        .catch(() => null)) as ApiErrorBody | null;
      const message = apiErrorMessage(body, fallback);
      throw response.status < 500
        ? new RefusedRequestError(message, response.status)
        : new Error(message);
    }
    return response.json() as Promise<T>;
  };
};

export const useAgentChatCounts = () => {
  const request = useProactiveAgentsApi();
  return useQuery({
    queryKey: ['proactive-agents', 'agents'],
    queryFn: () =>
      request<AgentChatCount[]>('/agents', 'Failed to load agents'),
  });
};

export const useNudgeRecipients = (target: PreviewTarget | null) => {
  const request = useProactiveAgentsApi();
  return useQuery({
    queryKey: ['proactive-agents', 'nudges', 'recipients', target],
    queryFn: () =>
      request<NudgeRecipients>(
        '/nudges/recipients',
        'Failed to load recipients',
        {
          method: 'POST',
          body: JSON.stringify(target),
        }
      ),
    enabled: target !== null,
  });
};

const SLOW_STATUS_POLL_MS = 60_000;
// Successful fetches at the faster rate, about 10 minutes for a send and 3 for a change, before polling slows to once
// a minute. The count is kept with the page's cached query, so it starts again after a reload. Failed fetches and a
// hidden tab make the faster phase last longer.
const FAST_STATUS_POLLS = 60;

// How often the status of a send or a change polls while `isRunning` holds. A first status that failed to load is tried
// again once a minute, unless the server knows nothing under the id.
const pollWhileRunning =
  <T>(isRunning: (status: T) => boolean, fastPollMs: number) =>
  (query: Query<T>) => {
    const status = query.state.data;
    const { error } = query.state;
    if (status === undefined) {
      if (!error) return fastPollMs;
      return isNotFound(error) ? false : SLOW_STATUS_POLL_MS;
    }
    if (!isRunning(status)) return false;
    return query.state.dataUpdateCount < FAST_STATUS_POLLS
      ? fastPollMs
      : SLOW_STATUS_POLL_MS;
  };

// Each turn updates the status when it ends, which can take minutes, so the faster rate is every 10 seconds
const NUDGE_STATUS_POLL_MS = 10_000;

// Polls while any chat is still waiting for its turn to end
export const useNudgeStatusQuery = (nudgeId: string) => {
  const request = useProactiveAgentsApi();
  return useQuery({
    queryKey: ['proactive-agents', 'nudges', nudgeId],
    queryFn: () =>
      request<NudgeStatus>(
        `/nudges/${nudgeId}`,
        'Failed to load the delivery status'
      ),
    refetchInterval: pollWhileRunning<NudgeStatus>(
      (status) => status.waiting.length > 0,
      NUDGE_STATUS_POLL_MS
    ),
  });
};

export const useSendNudge = () => {
  const request = useProactiveAgentsApi();
  return useMutation({
    mutationFn: (input: SendNudgeRequest) =>
      request<SendNudgeResult>('/nudges', 'Failed to send the nudge', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  });
};

export const useBuiltinAgents = () => {
  const request = useProactiveAgentsApi();
  return useQuery({
    queryKey: ['proactive-agents', 'users', 'builtin-agents'],
    queryFn: () =>
      request<BuiltinAgents>(
        '/users/builtin-agents',
        'Failed to load the built-in agents'
      ),
  });
};

export const useUserPreview = (target: PreviewTarget | null) => {
  const request = useProactiveAgentsApi();
  return useQuery({
    queryKey: ['proactive-agents', 'users', 'preview', target],
    queryFn: () =>
      request<UserPreview>('/users/preview', 'Failed to load the users', {
        method: 'POST',
        body: JSON.stringify(target),
      }),
    enabled: target !== null,
  });
};

const changeRequest = (change: UserAgentChange): [string, object] => {
  const { agent, filter } = change;
  switch (change.action) {
    case 'set-default':
    case 'clear-default':
      return [
        '/default-agent',
        {
          ...filter,
          agent: change.action === 'set-default' ? agent : null,
          apply_now: change.applyNow,
          spread_minutes: change.spreadMinutes,
        },
      ];
    case 'pause':
    case 'resume':
      return [
        '/paused',
        { ...filter, agent, paused: change.action === 'pause' },
      ];
    case 'delete':
      return ['/delete-agent-chat', { ...filter, agent }];
  }
};

// A change can alter what every part of the page shows, such as the preview, the chat counts and the nudge recipients
const refreshPage = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: ['proactive-agents'] });

// Starts a change in the background under `changeId`
export const useChangeUsers = () => {
  const request = useProactiveAgentsApi();
  return useMutation({
    mutationFn: ({
      changeId,
      change,
    }: {
      changeId: string;
      change: UserAgentChange;
    }) => {
      const [path, body] = changeRequest(change);
      return request<UserChangeStarted>(
        `/users${path}`,
        'Failed to start the change',
        {
          method: 'POST',
          body: JSON.stringify({ change_id: changeId, ...body }),
        }
      );
    },
  });
};

const changeIsRunning = (status: UserChangeStatus) => status.counts.waiting > 0;

// What the page knows of a change from its status query. Not found means the server has no record of it, for example
// after a start request that failed before saving it. Unavailable means the status could not be loaded, so the change
// may be running.
export type ChangePhase =
  | 'loading'
  | 'not-found'
  | 'unavailable'
  | 'running'
  | 'finished';

export const changePhaseOf = (
  status: UserChangeStatus | undefined,
  error: Error | null
): ChangePhase => {
  if (status) return changeIsRunning(status) ? 'running' : 'finished';
  if (!error) return 'loading';
  return isNotFound(error) ? 'not-found' : 'unavailable';
};

// A user's result is recorded within seconds of the change reaching them, so the faster rate is every 3 seconds
const CHANGE_STATUS_POLL_MS = 3_000;

// How long after a change was confirmed its status may still be not found, while its start request saves it. A tab
// that did not send that request, or a page reloaded meanwhile, may ask before then.
const SAVE_GRACE_MS = 60_000;

// Polls while the change has not reached every user
export const useUserChangeStatusQuery = (
  change: { id: string; startedAt: number } | null
) => {
  const request = useProactiveAgentsApi();
  const queryClient = useQueryClient();
  const statusQuery = useQuery({
    queryKey: ['proactive-agents', 'users', 'changes', change?.id ?? null],
    queryFn: () =>
      request<UserChangeStatus>(
        `/users/changes/${change?.id}`,
        'Failed to load the change'
      ),
    enabled: change !== null,
    // While it retries the query has no error, so the page shows the change as loading and keeps the buttons locked
    retry: (failureCount, error) =>
      isNotFound(error) && change
        ? Date.now() - change.startedAt < SAVE_GRACE_MS
        : failureCount < 3,
    refetchInterval: pollWhileRunning(changeIsRunning, CHANGE_STATUS_POLL_MS),
  });
  const changeFinished =
    statusQuery.data !== undefined && !changeIsRunning(statusQuery.data);
  // Also runs once when the page loads a remembered change that has already finished, which only reloads fresh data
  useEffect(() => {
    if (changeFinished) void refreshPage(queryClient);
  }, [changeFinished, queryClient]);
  return statusQuery;
};
